import * as Sentry from '@sentry/react';
import React, { useEffect, useState, useRef } from 'react';
import type { Place, PlaceTranslation } from '../types/place';
import { useAppSelector } from '../redux/hooks';
import { selectDirection, selectCurrentLanguage, selectChildrenIds } from '../redux/selectors';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';


import styles from './GalleriesPage.module.css'
import Thumbnail from '../components/factories/Thumbnail';
import { useTheme, palette } from '../themes/theme';
import { getChildren } from '../services/data';
import { useParams, useOutletContext } from 'react-router-dom';
import { APP_AGAM, APP_ANNEFRANK, ANNEFRANK_CHAPTER_BREAKS } from '../constants/apps';
import ViewedProgress from '../components/common/ViewedProgress';
import { useViewedDataProgress } from '../hooks/useViewedDataProgress';
import { useTranslation } from 'react-i18next';

interface ChapterDividerProps {
  text: string;
}

const ChapterDivider: React.FC<ChapterDividerProps> = ({ text }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', my: 2 }}>
    <Box sx={{ flex: 1, height: 2, bgcolor: palette.text.primary, opacity: 1, mx: 1, borderRadius: 0 }} />
    <Typography
      sx={{
        fontFamily: 'Poppins',
        fontWeight: 600,
        fontSize: '14px',
        lineHeight: '20px',
        color: palette.text.primary,
        letterSpacing: 0,
        textAlign: 'center',
        mx: 2,
        whiteSpace: 'nowrap',
        borderRadius: 0,
        opacity: 1,
        textTransform: 'uppercase',
      }}
    >
      {text}
    </Typography>
    <Box sx={{ flex: 1, height: 2, bgcolor: palette.text.primary, opacity: 1, mx: 1, borderRadius: 0 }} />
  </Box>
);

interface DataDisplayProps {
    translations: PlaceTranslation[];
    parentId: string;
    parentTitle: string;
}

const DataDisplay: React.FC<DataDisplayProps> = ({translations, parentId, parentTitle}) => {
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [data, setData] = useState<Place[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const observerElem = useRef<HTMLDivElement | null>(null)
    const {slug} = useParams()
    const {isWithProgress} = useOutletContext<{ isWithProgress: boolean }>()
    const {shouldBeVisible, hasBeenViewed} = useViewedDataProgress(slug)
    const direction = useAppSelector(selectDirection)
    const currentLanguage = useAppSelector(selectCurrentLanguage)
    const childrenIds = useAppSelector(selectChildrenIds)
    const { t, i18n } = useTranslation();
    const theme = useTheme()

    const loadMoreChildren = async () => {
        if (!loading) {
            try {
                setLoading(true)
                const newChildren = await getChildren('Place', currentLanguage ?? 'en', childrenIds)
                if (newChildren.length > 0) {
                    setData(prev => [...prev, ...newChildren])
                    setCurrentPage(prev => prev + 1)
                }
            } catch (err) {
                Sentry.captureException(err);
            } finally {
                setLoading(false)
            }
        }
    }

    useEffect(() => {
        if (!window.IntersectionObserver) return () => {};
        const element = observerElem.current
        const option: IntersectionObserverInit = { threshold: 0.5 }

        const observer = new IntersectionObserver(handleObserver, option)
        if (element) observer.observe(element);
        return () => { if (element) observer.unobserve(element) }
    }, [currentPage, loading])

    useEffect(() => {
        if (childrenIds.length > 0) {
            loadMoreChildren()
        }
    }, [childrenIds, currentLanguage])

    const hasMoreData = () => {
        return data.length < childrenIds.length
    }

    const handleObserver = (entries: IntersectionObserverEntry[]) => {
        const [target] = entries;
        if (target.isIntersecting && hasMoreData() && !loading) {
            loadMoreChildren()
        }
    }

    const handleListItemClick = (_event: React.MouseEvent<HTMLUListElement>) => {
        // Station selection is tracked when DynamicPage loads the station
    }

    const getProgressIndex = (child: Place, index: number): number => {
        if (child.type === 'intro' || child.thumbnail_type === 'minor') {
            return -1;
        }
        
        let progressIndex = 0;
        for (let i = 0; i < index; i++) {
            const prevChild = data[i];
            if (prevChild.type !== 'intro' && prevChild.thumbnail_type !== 'minor') {
                progressIndex++;
            }
        }
        return progressIndex;
    };

    return (
        <div className={styles['galleries-page']} style={{direction: direction, }}>
            <div>
                {!isWithProgress && <Typography variant='h1'>
                    {translations[0].information_title}
                </Typography>}
                {!isWithProgress && <Typography variant='body1' sx={{mt: '0.5rem', mb: '1rem'}}>
                    {translations[0].information_subtitle}
                </Typography>}
                {isWithProgress && <ViewedProgress parentId={parentId} />}
            </div>
            <List sx={{
                    overflowY: 'auto', 
                    direction: 'ltr', 
                    mt: '2.7rem', 
                    p: 0, 
                    height: '100%',
                    padding: {sm: '0 8rem'},
                    gap: '5%',
                    }}
                    onClick={handleListItemClick}
                >
                {data.length === 0 && childrenIds.length > 0 && (
                    Array.from({ length: 4 }).map((_, i) => (
                        <ListItem key={`skeleton-${i}`} sx={{ p: 0, justifyContent: 'center', mb: '2rem' }}>
                            <Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 2 }} />
                        </ListItem>
                    ))
                )}
                {
                    data.map((child, index) => {
                        if (child.type === 'intro') {
                            return null;
                        }
                        
                        if (child.translations.length > 0) {
                            const progressIndex = getProgressIndex(child, index);
                            const enabled = progressIndex >= 0 ? shouldBeVisible(parentId, progressIndex) : true;
                            const wasViewed = hasBeenViewed(parentId, child.id)
                            
                            const chapterElements: React.ReactNode[] = [];
                            
                            if (slug === APP_ANNEFRANK && index in ANNEFRANK_CHAPTER_BREAKS) {
                                const translationKey = ANNEFRANK_CHAPTER_BREAKS[index];
                                chapterElements.push(
                                    <ListItem key={`chapter-${index}`} dir={direction} 
                                        sx={{
                                            p: 0,
                                            justifyContent: 'center',
                                            position: 'relative',
                                            mb: '1rem',
                                            mt: '1rem',
                                            background: 'transparent',
                                        }}
                                    >
                                        <ChapterDivider text={t(translationKey)} />
                                    </ListItem>
                                );
                            }
                            
                            chapterElements.push(
                                <ListItem key={child.id} dir={direction} 
                                    sx={{
                                        pointerEvents: 'all',
                                        transition: 'filter 0.3s ease, opacity 0.3s ease',
                                        p: 0,
                                        justifyContent: 'center',
                                        position: 'relative',
                                        '&:not(:last-child)': {
                                            mb: '2rem'
                                        },
                                        '&[data-item-title]': {
                                            title:child.information_title}
                                        }}
                                    {...{'data-item-title': child.information_title}}
                                    data-station-id={child.id}
                                    data-station-enabled={enabled}
                                    data-station-viewed={wasViewed}
                                >
                                    <Thumbnail props={child} enabled={enabled} wasViewed={wasViewed} />
                                </ListItem>
                            );
                            
                            return chapterElements;
                        }
                        return null
                        })
                }
                {
                    hasMoreData() && data.length !== 0 &&
                    <CircularProgress ref={observerElem} sx={{color: theme.primary}} />
                }
            </List>
        </div>
    );
};

export default DataDisplay;
